import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Search, Eye, Trash2, MessageCircle, 
  AlertCircle, CheckCircle2, Clock, XCircle, 
  Filter, Send, User, Calendar, Phone
} from 'lucide-react';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/ExportButton';

interface Ticket {
  id: number;
  ticketNumber: string;
  subject: string;
  description: string;
  type: string;
  priority: string;
  status: string;
  customerName: string | null;
  assignedTo: string | null;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
}

interface TicketDetail {
  id: number;
  ticketNumber: string;
  subject: string;
  description: string;
  type: string;
  priority: string;
  status: string;
  customerName: string | null;
  customerPhone: string | null;
  assignedTo: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  replies: {
    id: number;
    userName: string;
    message: string;
    isStaffReply: boolean;
    createdAt: string;
  }[];
}

const statusConfig: Record<string, { color: string; icon: any; label: string }> = {
  Open: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: AlertCircle, label: 'مفتوحة' },
  InProgress: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'قيد المعالجة' },
  Resolved: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'تم الحل' },
  Closed: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle, label: 'مغلقة' }
};

const priorityConfig: Record<string, { color: string; label: string }> = {
  Low: { color: 'bg-gray-100 text-gray-700', label: 'منخفضة' },
  Medium: { color: 'bg-yellow-100 text-yellow-700', label: 'متوسطة' },
  High: { color: 'bg-red-100 text-red-700', label: 'عالية' },
  Urgent: { color: 'bg-red-600 text-white', label: 'طارئة' }
};

const typeLabels: Record<string, string> = {
  General: 'عام',
  Tracking: 'تتبع',
  Delivery: 'توصيل',
  Refund: 'استرداد',
  Payment: 'دفع',
  Complaint: 'شكوى'
};

export default function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  const [form, setForm] = useState({
    subject: '',
    description: '',
    type: 'General',
    priority: 'Medium',
    customerId: ''
  });

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [statusFilter, priorityFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (search) params.append('search', search);
      
      const res = await api.get(`/api/tickets?${params.toString()}`);
      if (res.data.success) {
        setTickets(res.data.data.items);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/api/tickets/stats');
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const viewTicket = async (id: number) => {
    try {
      const res = await api.get(`/api/tickets/${id}`);
      if (res.data.success) {
        setSelectedTicket(res.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
    }
  };

  const createTicket = async () => {
    if (!form.subject || !form.description) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    try {
      const res = await api.post('/api/tickets', {
        ...form,
        customerId: form.customerId ? parseInt(form.customerId) : null
      });
      if (res.data.success) {
        alert('تم إنشاء التذكرة بنجاح');
        setShowCreateModal(false);
        setForm({ subject: '', description: '', type: 'General', priority: 'Medium', customerId: '' });
        loadTickets();
        loadStats();
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('حدث خطأ أثناء إنشاء التذكرة');
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      const res = await api.put(`/api/tickets/${id}`, { status });
      if (res.data.success) {
        loadTickets();
        loadStats();
        if (selectedTicket && selectedTicket.id === id) {
          viewTicket(id);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSendingReply(true);
    try {
      const res = await api.post(`/api/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage,
        userName: 'الموظف',
        isStaffReply: true
      });
      if (res.data.success) {
        setReplyMessage('');
        viewTicket(selectedTicket.id);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSendingReply(false);
    }
  };

  const deleteTicket = async (id: number) => {
    if (!confirm('هل أنت متأكد من حذف هذه التذكرة؟')) return;
    try {
      const res = await api.delete(`/api/tickets/${id}`);
      if (res.data.success) {
        loadTickets();
        loadStats();
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  const exportColumns = [
    { key: 'ticketNumber', label: 'رقم التذكرة' },
    { key: 'subject', label: 'الموضوع' },
    { key: 'type', label: 'النوع', format: (v: string) => typeLabels[v] || v },
    { key: 'priority', label: 'الأولوية', format: (v: string) => priorityConfig[v]?.label || v },
    { key: 'status', label: 'الحالة', format: (v: string) => statusConfig[v]?.label || v },
    { key: 'customerName', label: 'العميل' },
    { key: 'assignedTo', label: 'مُسند إلى' },
    { key: 'replyCount', label: 'عدد الردود' },
    { key: 'createdAt', label: 'التاريخ', format: (v: string) => new Date(v).toLocaleDateString('ar-EG') }
  ];

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            نظام تذاكر الدعم
          </h1>
          <p className="text-muted-foreground mt-1">إدارة تذاكر الدعم الفني وخدمة العملاء</p>
        </div>
        <div className="flex gap-2">
          <ExportButton data={tickets} filename="tickets" columns={exportColumns} title="تذاكر الدعم" />
          <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700 gap-2">
            <Plus className="w-4 h-4" />
            تذكرة جديدة
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">مفتوحة</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.open}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">قيد المعالجة</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.inProgress}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">تم الحل</p>
                  <p className="text-2xl font-bold text-green-900">{stats.resolved}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700">الإجمالي</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <MessageCircle className="w-8 h-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="بحث بالرقم أو الموضوع..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadTickets()}
                className="pr-10"
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg bg-white">
              <option value="">كل الحالات</option>
              <option value="Open">مفتوحة</option>
              <option value="InProgress">قيد المعالجة</option>
              <option value="Resolved">تم الحل</option>
              <option value="Closed">مغلقة</option>
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="px-4 py-2 border rounded-lg bg-white">
              <option value="">كل الأولويات</option>
              <option value="Low">منخفضة</option>
              <option value="Medium">متوسطة</option>
              <option value="High">عالية</option>
            </select>
            <Button onClick={loadTickets} variant="outline">
              <Filter className="w-4 h-4 ml-2" />
              تطبيق
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p>جاري التحميل...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد تذاكر</p>
            </div>
          ) : (
            <div className="divide-y">
              {tickets.map((ticket) => {
                const status = statusConfig[ticket.status] || statusConfig['Open'];
                const priority = priorityConfig[ticket.priority] || priorityConfig['Medium'];
                const StatusIcon = status.icon;
                return (
                  <div key={ticket.id} className="p-4 hover:bg-gray-50 transition">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-mono text-sm text-gray-500">{ticket.ticketNumber}</span>
                          <Badge className={priority.color}>{priority.label}</Badge>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 ml-1" />
                            {status.label}
                          </Badge>
                          <Badge variant="outline">{typeLabels[ticket.type] || ticket.type}</Badge>
                        </div>
                        <h3 className="font-semibold text-lg mb-1">{ticket.subject}</h3>
                        <p className="text-sm text-gray-600 line-clamp-1">{ticket.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500 flex-wrap">
                          {ticket.customerName && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.customerName}
                            </span>
                          )}
                          {ticket.assignedTo && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              مُسند إلى: {ticket.assignedTo}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(ticket.createdAt).toLocaleDateString('ar-EG')}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {ticket.replyCount} رد
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => viewTicket(ticket.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {ticket.status === 'Open' && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(ticket.id, 'InProgress')} className="text-yellow-600">
                            <Clock className="w-4 h-4" />
                          </Button>
                        )}
                        {(ticket.status === 'Open' || ticket.status === 'InProgress') && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(ticket.id, 'Resolved')} className="text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => deleteTicket(ticket.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>إنشاء تذكرة جديدة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>الموضوع *</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="عنوان مختصر للمشكلة" />
              </div>
              <div>
                <Label>الوصف *</Label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="وصف تفصيلي للمشكلة..." className="w-full px-3 py-2 border rounded-lg min-h-[100px] focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>النوع</Label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="General">عام</option>
                    <option value="Tracking">تتبع</option>
                    <option value="Delivery">توصيل</option>
                    <option value="Refund">استرداد</option>
                    <option value="Payment">دفع</option>
                    <option value="Complaint">شكوى</option>
                  </select>
                </div>
                <div>
                  <Label>الأولوية</Label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="Low">منخفضة</option>
                    <option value="Medium">متوسطة</option>
                    <option value="High">عالية</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={createTicket} className="flex-1 bg-purple-600 hover:bg-purple-700">إنشاء التذكرة</Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">إلغاء</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showDetailModal && selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle>{selectedTicket.subject}</CardTitle>
                  <p className="text-sm text-gray-500 font-mono mt-1">{selectedTicket.ticketNumber}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowDetailModal(false)}>إغلاق</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">الحالة</p>
                  <Badge className={`mt-1 ${statusConfig[selectedTicket.status]?.color || ''}`}>{statusConfig[selectedTicket.status]?.label}</Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">الأولوية</p>
                  <Badge className={`mt-1 ${priorityConfig[selectedTicket.priority]?.color || ''}`}>{priorityConfig[selectedTicket.priority]?.label}</Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">النوع</p>
                  <p className="font-semibold mt-1">{typeLabels[selectedTicket.type]}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">التاريخ</p>
                  <p className="font-semibold mt-1 text-sm">{new Date(selectedTicket.createdAt).toLocaleDateString('ar-EG')}</p>
                </div>
              </div>

              {selectedTicket.customerName && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2"><User className="w-4 h-4" />معلومات العميل</h4>
                  <p className="text-sm">{selectedTicket.customerName}</p>
                  {selectedTicket.customerPhone && (
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{selectedTicket.customerPhone}</p>
                  )}
                </div>
              )}

              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">الوصف</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />المحادثة ({selectedTicket.replies.length})
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedTicket.replies.map((reply) => (
                    <div key={reply.id} className={`p-3 rounded-lg ${reply.isStaffReply ? 'bg-purple-50 border border-purple-200 mr-8' : 'bg-gray-50 border border-gray-200 ml-8'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm">
                          {reply.userName}
                          {reply.isStaffReply && <Badge className="mr-2 text-xs bg-purple-100 text-purple-700">موظف</Badge>}
                        </span>
                        <span className="text-xs text-gray-500">{new Date(reply.createdAt).toLocaleString('ar-EG')}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  ))}
                </div>

                {selectedTicket.status !== 'Closed' && (
                  <div className="mt-4 flex gap-2">
                    <Input placeholder="اكتب رداً..." value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendReply()} />
                    <Button onClick={sendReply} disabled={!replyMessage.trim() || sendingReply} className="bg-purple-600 hover:bg-purple-700">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t flex-wrap">
                {selectedTicket.status === 'Open' && (
                  <Button variant="outline" onClick={() => { updateStatus(selectedTicket.id, 'InProgress'); setShowDetailModal(false); }} className="text-yellow-600">
                    <Clock className="w-4 h-4 ml-2" />بدء المعالجة
                  </Button>
                )}
                {(selectedTicket.status === 'Open' || selectedTicket.status === 'InProgress') && (
                  <Button variant="outline" onClick={() => { updateStatus(selectedTicket.id, 'Resolved'); setShowDetailModal(false); }} className="text-green-600">
                    <CheckCircle2 className="w-4 h-4 ml-2" />تم الحل
                  </Button>
                )}
                {selectedTicket.status !== 'Closed' && (
                  <Button variant="outline" onClick={() => { updateStatus(selectedTicket.id, 'Closed'); setShowDetailModal(false); }} className="text-gray-600">
                    <XCircle className="w-4 h-4 ml-2" />إغلاق
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
